"use client";

import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import OTCHistoryDetailsView from "../../MedicalHistory/views/scriptHistoryDetails.jsx";
import PatientSignatureModal from "../../../global/view/patientSignature";
import DoctorePrescription from "../../../global/view/doctorePrescription";
import { fetchNextOtcNumber } from "../../../../api/otcDirect/otcDirectApi";
import { createProcessPrescription, discardRejectedPrescription } from "../../../../api/dispensary/script/processScriptApi";
import { submitClaim as submitMedikreditClaim } from "../../../../api/medikredit/claimApi";
import ClaimResultModal from "../../../../components/ClaimResultModal";
import { useAuth } from "../../../../context/AuthContext";

export default function OTCPaymentSummary() {
  const location = useLocation();
  const navigate = useNavigate();
  const { scriptData, continueAfterMedicalAid } = location.state || {};
  const { user, token } = useAuth();

  const [showPatientSig, setShowPatientSig] = useState(false);
  const [pharmacistSig, setPharmacistSig] = useState(null);
  const [patientSig, setPatientSig] = useState(null);
  const [showDoctorModal, setShowDoctorModal] = useState(false);
  const [nextOtcNumber, setNextOtcNumber] = useState(null);
  const [doctorPreview, setDoctorPreview] = useState(null);
  const [paymentMeta, setPaymentMeta] = useState(null);

  const [showClaimResult, setShowClaimResult] = useState(false);
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimResult, setClaimResult] = useState(null);
  const [claimError, setClaimError] = useState(null);
  const [claimPrescriptionId, setClaimPrescriptionId] = useState(null);
  const [discardingClaim, setDiscardingClaim] = useState(false);

  const isMedicalAid = (scriptData?.paymentType || "").toLowerCase() === "medical aid";

  const todayIsoDate = useMemo(() => {
    try {
      return new Date().toISOString().split("T")[0];
    } catch {
      return undefined;
    }
  }, []);

  const getUserIdFromToken = () => {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload?.userid || null;
    } catch {
      return null;
    }
  };

  // Shared totals for the OTC-direct flow. Prefer the grossAmount computed in
  // OTCHistoryDetailsView (paymentMeta.grossAmount) and only fall back to
  // scriptData.total. Amount/VAT use scriptData when available.
  const totals = {
    amount: scriptData?.amount ?? "",
    vat: scriptData?.vat ?? "",
    total: paymentMeta?.grossAmount
      ? `R ${paymentMeta.grossAmount}`
      : scriptData?.total
      ? `R${scriptData.total.toFixed?.(2) ?? scriptData.total}`
      : "",
  };

  const handleCreateOtcPrescription = async () => {
    const sd = scriptData || {};
    const pharmacistid = getUserIdFromToken();

    const items = Array.isArray(sd.drugs)
      ? sd.drugs
          .filter((m) => m && m.sku && m.saved !== false)
          .map((m) => ({
            sku: m.sku,
            quantity: m.quantity,
            stockQty: m.stockQty,
            toFollowStruck: m.toFollowStruck,
            instructions: m.instructions,
            instructionsOther: m.instructionsOther,
            deliveryOption: m.deliveryOption,
          }))
      : [];

    const payload = {
      profilenumber: sd.profileNo || null,
      patientid: sd.patientId || null,
      practitionerid: sd.practitionerId || null,
      pharmacistid: pharmacistid || null,
      paymenttype: sd.paymentType || null,
      chronic: false,
      repeatscript: 0,
      cashpricingoption: sd.scriptType || "OTC",
      processscriptnumber: null,
      otcReference: nextOtcNumber || null,
      branchcode: sd.branchcode || user?.branchcode || null,
      docurl: null,
      deliveryoption: "Collect",
      preferreddate: null,
      preferredtime: null,
      dispensingfee: null,
      gross: null,
      items,
      amendFlow: false,
      amendOriginalPrescriptionId: null,
      amendRemovedDrugs: [],
      scriptType: "OTC",
    };

    const resp = await createProcessPrescription(payload);
    if (resp?.success === false) {
      throw new Error(resp?.message || "Failed to create OTC prescription");
    }
    return resp?.data?.prescriptionid || null;
  };

  // Mark that we navigated from OTC summary so the form preserves draft when returning
  useEffect(() => {
    try { sessionStorage.setItem("cameFromOTCSummary", "1"); } catch {}
    return () => {
      // Do not clear here; the form will manage clearing based on this marker
    };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const n = await fetchNextOtcNumber();
        if (alive && n) setNextOtcNumber(String(n));
      } catch (e) {
        console.error("Failed to fetch next OTC number", e);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (continueAfterMedicalAid) {
      if (location.state?.pharmacistSignature) {
        setPharmacistSig(location.state.pharmacistSignature);
      }
      setShowPatientSig(true);
    }
  }, [continueAfterMedicalAid, location.state]);

  return (
    <>
      <OTCHistoryDetailsView
        scriptData={{
          ...(scriptData || {}),
          // For OTC preview, always prefer the allocated OTC reference. Avoid
          // falling back to the generated SCR_* placeholder from NewScriptForm.
          ...(nextOtcNumber
            ? {
                scriptNumber: nextOtcNumber,
                otcReference: nextOtcNumber,
              }
            : {}),
        }}
        scriptNumberLabel="OTC Number"
        showTotalItems={false}
        showPharmacistReadOnly={true}
        showPaymentTypeReadOnly={true}
        showPractitionerReadOnly={true}
        rowsPerPage={1000}
        invoiceActionLabel="Sign Invoice"
        minPreferredDate={todayIsoDate}
        hideViewScriptDocument={true}
        hidePackageLabel={true}
        centerTopControls={true}
        topControlsColumns={4}
        onPreviewDataChange={(data) => {
          setDoctorPreview(data);
          if (data?.paymentMeta) {
            setPaymentMeta(data.paymentMeta);
          }
        }}
        onInvoiceSigned={(sig) => {
          setPharmacistSig(sig);
          setShowPatientSig(true);
        }}
      />

      <PatientSignatureModal
        isOpen={showPatientSig}
        onClose={() => setShowPatientSig(false)}
        onConfirm={async (dataUrl) => {
          try {
            setPatientSig(dataUrl);
            const prescriptionId = await handleCreateOtcPrescription();
            setShowPatientSig(false);
            setClaimPrescriptionId(prescriptionId);

            if (isMedicalAid && prescriptionId) {
              setClaimResult(null);
              setClaimError(null);
              setClaimSubmitting(true);
              setShowClaimResult(true);
              try {
                const claim = await submitMedikreditClaim(prescriptionId);
                setClaimResult(claim);
                const rejected = claim && claim.approved === false && claim.resultText !== "warning";
                if (rejected) {
                  try {
                    await discardRejectedPrescription(prescriptionId);
                  } catch (discardErr) {
                    console.error("Failed to discard rejected OTC prescription:", discardErr);
                  }
                }
              } catch (e) {
                setClaimError(e?.response?.data?.message || e.message || "Claim submission failed");
              } finally {
                setClaimSubmitting(false);
              }
            } else {
              setShowDoctorModal(true);
            }
          } catch (e) {
            console.error("Failed to create OTC prescription on patient confirm:", e);
          }
        }}
        initialSignature={patientSig}
        totals={totals}
      />

      <DoctorePrescription
        isOpen={showDoctorModal}
        onClose={() => setShowDoctorModal(false)}
        patientInfo={doctorPreview?.patientInfo}
        prescription={doctorPreview?.prescription}
        pharmacistSignature={pharmacistSig}
        patientSignature={patientSig}
        totals={totals}
        useCompletedPopup={true}
      />

      {showClaimResult && (
        <ClaimResultModal
          submitting={claimSubmitting}
          result={claimResult}
          error={claimError}
          discarding={discardingClaim}
          onDiscard={async () => {
            if (!claimPrescriptionId) return;
            setDiscardingClaim(true);
            try {
              await discardRejectedPrescription(claimPrescriptionId);
            } catch (discardErr) {
              console.error("Failed to discard OTC claim:", discardErr);
            } finally {
              setDiscardingClaim(false);
              setShowClaimResult(false);
              navigate("/customer/dispensary/otc-direct/dispense-table", { replace: true });
            }
          }}
          onContinue={() => {
            setShowClaimResult(false);
            const rejected =
              claimResult && claimResult.approved === false && claimResult.resultText !== "warning";
            if (rejected) {
              navigate("/customer/dispensary/otc-direct/dispense-table", { replace: true });
            } else {
              setShowDoctorModal(true);
            }
          }}
        />
      )}
    </>
  );
}

